import React, { useState } from 'react';
import { getFeatureComparison, getUpgradeFeatures } from '../../../shared/config/planFeatures';
import { SUBSCRIPTION_COLORS } from './subscriptionColors';
import { GiftCodeSection } from './GiftCodeSection';

export const ChildSubscriptionModal = ({ child, family, mode, onClose, t, formatNumber, activityCounts = null }) => {
  const [isYearly, setIsYearly] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isSubscribe = mode === 'subscribe';
  const isUpgrade = mode === 'upgrade';
  const translationKey = isSubscribe ? 'subscribe_modal' : 'extend_modal';

  const formatCurrency = (amount) => {
    return formatNumber(amount) + '\u20AB';
  };

  const hasCashback = family.cashback_percentage > 0;
  const discountInfo = family.discount_percentage > 0
    ? t(`pricing_models.${family.pricing_model}`)
    : null;

  // Get pricing for a package and period
  const getPrice = (pkg, period) => {
    const prefix = pkg === 'premium' ? 'premium_' : '';
    return family[`${prefix}${period}_payment_amount`];
  };

  const getPriceBeforeCashback = (pkg, period) => {
    const prefix = pkg === 'premium' ? 'premium_' : '';
    return family[`${prefix}${period}_payment_amount_before_cashback`];
  };

  const getCashbackAmount = (pkg, period) => {
    const prefix = pkg === 'premium' ? 'premium_' : '';
    return family[`${prefix}${period}_cashback_amount`];
  };

  // Get base price before any discounts (pricing model or cashback)
  const getBasePrice = (pkg, period) => {
    const prefix = pkg === 'premium' ? 'premium_' : 'pro_';
    return family[`${prefix}${period}_base_price`];
  };

  // Calculate pricing model discount amount (base price - price before cashback)
  const getPricingModelDiscount = (pkg, period) => {
    const basePrice = getBasePrice(pkg, period);
    const priceBeforeCashback = getPriceBeforeCashback(pkg, period);
    return basePrice - priceBeforeCashback;
  };

  // Calculate daily rate
  const getDailyRate = (price, isYearlyPeriod) => {
    const days = isYearlyPeriod ? 365 : 31;
    return Math.round(price / days);
  };

  const currentPeriod = isYearly ? 'yearly' : 'monthly';
  const currentDays = isYearly ? 365 : 31;

  // Feature list from shared config with dynamic activity counts
  const features = getFeatureComparison(t, 'plan_features', activityCounts);

  const handleSubmit = async (pkg) => {
    setIsSubmitting(true);

    const form = document.createElement('form');
    form.method = 'POST';
    form.action = '/payments';

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
    if (csrfToken) {
      const csrfInput = document.createElement('input');
      csrfInput.type = 'hidden';
      csrfInput.name = 'authenticity_token';
      csrfInput.value = csrfToken;
      form.appendChild(csrfInput);
    }

    const addField = (name, value) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      form.appendChild(input);
    };

    addField('payment[child_id]', child.id);
    addField('payment[subscription_days]', currentDays);
    addField('payment[subscription_package]', pkg);
    addField('payment[order_description]', isSubscribe
      ? `Thuê bao ${pkg === 'premium' ? 'Premium' : 'Pro'} cho ${child.username} - ${currentDays} ngày`
      : `Gia hạn thuê bao ${pkg === 'premium' ? 'Premium' : 'Pro'} cho ${child.username} - ${currentDays} ngày`
    );

    document.body.appendChild(form);
    form.submit();
  };

  const handleUpgradeSubmit = async () => {
    setIsSubmitting(true);

    const form = document.createElement('form');
    form.method = 'POST';
    form.action = '/payments';

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
    if (csrfToken) {
      const csrfInput = document.createElement('input');
      csrfInput.type = 'hidden';
      csrfInput.name = 'authenticity_token';
      csrfInput.value = csrfToken;
      form.appendChild(csrfInput);
    }

    const addField = (name, value) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      form.appendChild(input);
    };

    addField('payment[child_id]', child.id);
    addField('payment[payment_type]', 'upgrade');
    addField('payment[subscription_package]', 'premium');
    addField('payment[order_description]', `Nâng cấp lên Premium cho ${child.username}`);

    document.body.appendChild(form);
    form.submit();
  };

  // Pricing card component with action button
  const PricingCard = ({ pkg, badge }) => {
    const price = getPrice(pkg, currentPeriod);
    const priceBeforeCashback = getPriceBeforeCashback(pkg, currentPeriod);
    const cashback = getCashbackAmount(pkg, currentPeriod);
    const basePrice = getBasePrice(pkg, currentPeriod);
    const pricingModelDiscount = getPricingModelDiscount(pkg, currentPeriod);
    const dailyRate = getDailyRate(price, isYearly);
    const isPremiumPkg = pkg === 'premium';
    const colors = isPremiumPkg ? SUBSCRIPTION_COLORS.premium : SUBSCRIPTION_COLORS.pro;

    // Check if we have any discounts
    const hasDiscounts = pricingModelDiscount > 0 || cashback > 0;

    // Get button label
    const buttonLabel = t(`dashboard.index.children.${translationKey}.subscribe_to_plan`, { plan: isPremiumPkg ? 'Premium' : 'Pro' })
      || `Đăng ký ${isPremiumPkg ? 'Premium' : 'Pro'}`;

    return (
      <div
        className="card h-100 position-relative"
        style={{
          borderColor: colors.border || colors.badge.color,
          borderWidth: '2px',
          borderRadius: '16px',
          boxShadow: colors.shadow ? `0px 8px 10px 0px ${colors.shadow}, 0px 20px 25px 0px ${colors.shadow}` : 'none',
          overflow: 'visible'
        }}
      >
        {badge && (
          <div
            className="position-absolute start-50 translate-middle-x"
            style={{ top: '-12px', zIndex: 1 }}
          >
            <span className="badge px-3 py-2" style={{ fontSize: '11px', ...colors.badge }}>
              {badge}
            </span>
          </div>
        )}
        <div className="card-body pt-4 d-flex flex-column">
          <h6 className="text-center text-muted mb-2">{isPremiumPkg ? 'Premium' : 'Pro'}</h6>

          {/* Price Breakdown Section */}
          <div className="mb-3" data-testid={`price-breakdown-${isPremiumPkg ? 'premium' : 'pro'}`}>
            {hasDiscounts ? (
              <div className="small">
                {/* Original price */}
                <div className="d-flex justify-content-between mb-1">
                  <span className="text-muted">{t('dashboard.index.children.subscribe_modal.original_price') || 'Giá gốc'}:</span>
                  <span className="text-decoration-line-through text-muted" data-testid={`original-price-${isPremiumPkg ? 'premium' : 'pro'}`}>{formatCurrency(basePrice)}</span>
                </div>

                {/* Pricing model discount */}
                {pricingModelDiscount > 0 && (
                  <div className="d-flex justify-content-between mb-1">
                    <span className="text-muted text-start">
                      <i className="bi bi-tag-fill me-1" style={{ fontSize: '10px' }}></i>
                      {discountInfo}:
                    </span>
                    <span className="text-danger text-nowrap ms-2" data-testid={`pricing-discount-${isPremiumPkg ? 'premium' : 'pro'}`}>-{formatCurrency(pricingModelDiscount)}</span>
                  </div>
                )}

                {/* Referral cashback */}
                {cashback > 0 && (
                  <div
                    className="d-flex justify-content-between mb-1"
                    data-testid={`cashback-amount-${isPremiumPkg ? 'premium' : 'pro'}`}
                    data-cashback={cashback}
                  >
                    <span className="text-muted text-start">
                      <i className="bi bi-gift-fill me-1" style={{ fontSize: '10px' }}></i>
                      {t('dashboard.index.children.subscribe_modal.referral_discount') || 'Giảm giá giới thiệu'}:
                    </span>
                    <span className="text-danger text-nowrap ms-2" data-testid={`cashback-discount-${isPremiumPkg ? 'premium' : 'pro'}`}>-{formatCurrency(cashback)}</span>
                  </div>
                )}

                {/* Divider */}
                <hr className="my-2" />

                {/* Final price with daily rate */}
                <div className="d-flex justify-content-between align-items-baseline">
                  <span className="fw-semibold">{t('dashboard.index.children.subscribe_modal.final_price') || 'Thành tiền'}:</span>
                  <div className="text-end">
                    <span
                      className="fw-bold"
                      style={{ color: colors.badge.color }}
                      data-testid={`final-price-${isPremiumPkg ? 'premium' : 'pro'}`}
                    >
                      {formatCurrency(price)}
                    </span>
                    <div className="text-muted" style={{ fontSize: '11px' }}>
                      (~{formatNumber(dailyRate)}₫/{t('dashboard.index.children.subscribe_modal.per_day') || 'ngày'})
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              /* No discounts - simple price display */
              <div className="text-center">
                <span
                  className="fs-4 fw-bold"
                  style={{ color: colors.badge.color }}
                  data-testid={`final-price-${isPremiumPkg ? 'premium' : 'pro'}`}
                >
                  {formatCurrency(price)}
                </span>
                <div className="text-muted small">
                  (~{formatNumber(dailyRate)}₫/{t('dashboard.index.children.subscribe_modal.per_day') || 'ngày'})
                </div>
              </div>
            )}
          </div>

          <ul className="list-unstyled small mb-3 flex-grow-1">
            {features.map((feature) => {
              const isIncluded = isPremiumPkg ? feature.premium : feature.pro;
              return (
                <li key={feature.key} className="mb-2 d-flex align-items-start">
                  {isIncluded ? (
                    <i className="bi bi-check-circle-fill text-success me-2 mt-1" style={{ fontSize: '14px' }}></i>
                  ) : (
                    <i className="bi bi-x-circle-fill text-danger me-2 mt-1" style={{ fontSize: '14px' }}></i>
                  )}
                  <span className={isIncluded ? '' : 'text-muted'}>{feature.label}</span>
                </li>
              );
            })}
          </ul>

          {/* Action button */}
          <button
            type="button"
            className="btn w-100 mt-auto"
            style={{
              ...(isPremiumPkg
                ? { background: colors.buttonGradient, color: colors.button.color }
                : colors.button),
              border: 'none',
              borderRadius: '12px',
              padding: '12px 16px'
            }}
            onClick={() => handleSubmit(pkg)}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            ) : (
              <>
                {buttonLabel}
                <i className="bi bi-arrow-right ms-2"></i>
              </>
            )}
          </button>
        </div>
      </div>
    );
  };

  // Upgrade mode - show comparison between Pro and Premium plans
  if (isUpgrade) {
    const proFeatures = features.filter(f => f.pro);
    const premiumExclusiveFeatures = features.filter(f => f.premium && !f.pro);
    const remainingDays = child.remaining_subscription_days || 0;

    return (
      <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                {t('dashboard.index.children.upgrade_modal.title') || 'Nâng Cấp Lên Premium'}
              </h5>
              <button type="button" className="btn-close" onClick={onClose}></button>
            </div>
            <div className="modal-body">
              <p className="text-muted text-center mb-4">
                {t('dashboard.index.children.upgrade_modal.description') || 'Nâng cấp tài khoản để truy cập các tính năng cao cấp cho'}
                {' '}<strong>{child.username}</strong>
              </p>

              {/* Plan comparison cards - vertical on mobile, 2 columns on desktop */}
              <div className="row g-3" style={{ marginTop: '12px' }}>
                {/* Current Pro Plan */}
                <div className="col-12">
                  <div
                    className="card h-100 position-relative"
                    style={{
                      borderColor: SUBSCRIPTION_COLORS.pro.border,
                      borderWidth: '2px',
                      borderRadius: '16px',
                      boxShadow: `0px 8px 10px 0px ${SUBSCRIPTION_COLORS.pro.shadow}, 0px 20px 25px 0px ${SUBSCRIPTION_COLORS.pro.shadow}`,
                      overflow: 'visible'
                    }}
                  >
                    {/* Badge overlapping border */}
                    <div className="position-absolute start-50 translate-middle-x" style={{ top: '-12px', zIndex: 1 }}>
                      <span className="badge px-3 py-1" style={{ fontSize: '12px', ...SUBSCRIPTION_COLORS.pro.badge }}>
                        {t('dashboard.index.children.upgrade_modal.current_plan') || 'Gói hiện tại'}
                      </span>
                    </div>
                    <div className="card-body pt-4 d-flex flex-column">
                      <div className="d-flex align-items-center justify-content-center flex-wrap gap-2 mb-3">
                        <h6 className="card-title mb-0" style={{ color: SUBSCRIPTION_COLORS.pro.badge.color }}>
                          <i className="bi bi-star me-2"></i>
                          Pro
                        </h6>
                        <span className="badge bg-light text-dark border" style={{ fontSize: '10px' }}>
                          {child.subscription_billing_cycle === 'yearly'
                            ? (t('dashboard.index.children.subscription_history.yearly') || 'Yearly')
                            : (t('dashboard.index.children.subscription_history.monthly') || 'Monthly')
                          }
                        </span>
                      </div>
                      <ul className="list-unstyled small mb-3 flex-grow-1">
                        {proFeatures.map((feature) => (
                          <li key={feature.key} className="mb-2 d-flex align-items-start">
                            <i className="bi bi-check-circle-fill text-success me-2 mt-1" style={{ fontSize: '12px' }}></i>
                            <span>{feature.label}</span>
                          </li>
                        ))}
                        {premiumExclusiveFeatures.map((feature) => (
                          <li key={feature.key} className="mb-2 d-flex align-items-start">
                            <i className="bi bi-x-circle-fill text-danger me-2 mt-1" style={{ fontSize: '12px' }}></i>
                            <span className="text-muted">{feature.label}</span>
                          </li>
                        ))}
                      </ul>
                      {/* Remaining days and current plan button */}
                      <div className="border-top pt-3 mt-auto">
                        <div className="text-center mb-3">
                          <i className="bi bi-clock text-muted me-1" style={{ fontSize: '12px' }}></i>
                          <span className="text-muted small">
                            {t('dashboard.index.children.upgrade_modal.remaining_days', { days: remainingDays }) || `Còn ${remainingDays} ngày`}
                          </span>
                        </div>
                        <button
                          type="button"
                          className="btn btn-secondary w-100"
                          disabled
                          style={{ borderRadius: '12px' }}
                        >
                          {t('dashboard.index.children.upgrade_modal.current_plan') || 'Gói hiện tại'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Premium Plan */}
                <div className="col-12">
                  <div
                    className="card h-100 position-relative"
                    style={{
                      borderColor: SUBSCRIPTION_COLORS.premium.border,
                      borderWidth: '2px',
                      borderRadius: '16px',
                      boxShadow: `0px 8px 10px 0px ${SUBSCRIPTION_COLORS.premium.shadow}, 0px 20px 25px 0px ${SUBSCRIPTION_COLORS.premium.shadow}`,
                      overflow: 'visible'
                    }}
                  >
                    {/* Badge overlapping border */}
                    <div className="position-absolute start-50 translate-middle-x" style={{ top: '-12px', zIndex: 1 }}>
                      <span className="badge px-3 py-1" style={{ fontSize: '12px', ...SUBSCRIPTION_COLORS.premium.badge }}>
                        {t('dashboard.index.children.subscribe_modal.most_complete') || 'Đầy Đủ Nhất'}
                      </span>
                    </div>
                    <div className="card-body pt-4 d-flex flex-column">
                      <div className="d-flex align-items-center justify-content-center flex-wrap gap-2 mb-3">
                        <h6 className="card-title mb-0" style={{ color: SUBSCRIPTION_COLORS.premium.badge.color }}>
                          <i className="bi bi-star-fill me-2"></i>
                          Premium
                        </h6>
                        <span className="badge bg-light text-dark border" style={{ fontSize: '10px' }}>
                          {child.subscription_billing_cycle === 'yearly'
                            ? (t('dashboard.index.children.subscription_history.yearly') || 'Yearly')
                            : (t('dashboard.index.children.subscription_history.monthly') || 'Monthly')
                          }
                        </span>
                      </div>
                      <ul className="list-unstyled small mb-3 flex-grow-1">
                        {features.filter(f => f.premium).map((feature) => (
                          <li key={feature.key} className="mb-2 d-flex align-items-start">
                            <i className="bi bi-check-circle-fill text-success me-2 mt-1" style={{ fontSize: '12px' }}></i>
                            <span>{feature.label}</span>
                          </li>
                        ))}
                      </ul>

                      {/* Upgrade cost and button inside Premium card */}
                      <div className="border-top pt-3 mt-auto">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <span className="text-muted small">
                            {t('dashboard.index.children.upgrade_modal.price_difference') || 'Chi phí nâng cấp'}
                          </span>
                          <span className="fw-bold fs-5" style={{ color: SUBSCRIPTION_COLORS.premium.badge.color }}>
                            {formatCurrency(child.upgrade_cost_to_premium || 0)}
                          </span>
                        </div>
                        <button
                          type="button"
                          className="btn w-100"
                          style={{
                            background: SUBSCRIPTION_COLORS.premium.buttonGradient,
                            color: SUBSCRIPTION_COLORS.premium.button.color,
                            border: 'none',
                            borderRadius: '12px'
                          }}
                          onClick={handleUpgradeSubmit}
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                              ...
                            </>
                          ) : (
                            <>
                              {t('dashboard.index.children.upgrade_modal.proceed_to_upgrade') || 'Tiến Hành Nâng Cấp'}
                              <i className="bi bi-arrow-right ms-2"></i>
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Note about end date */}
              <p className="text-muted small text-center mt-3 mb-0">
                <i className="bi bi-info-circle me-1"></i>
                {t('dashboard.index.children.upgrade_modal.keeps_same_end_date') || 'Ngày hết hạn sẽ không thay đổi sau khi nâng cấp'}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              {isSubscribe
                ? t('dashboard.index.children.subscribe_modal.title')
                : t('dashboard.index.children.extend_modal.title')
              }
            </h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          <div className="modal-body">
            <p className="text-muted text-center mb-3">
              {t(`dashboard.index.children.${translationKey}.description`)}
              {' '}<strong>{child.username}</strong>
            </p>

            {/* Monthly/Yearly Toggle */}
            <div className="d-flex justify-content-center align-items-center gap-3 mb-4">
              <span className={`fw-semibold ${!isYearly ? 'text-primary' : 'text-muted'}`}>
                {t('dashboard.index.children.subscribe_modal.monthly') || 'Tháng'}
              </span>
              <div className="form-check form-switch mb-0">
                <input
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  id="billingToggle"
                  checked={isYearly}
                  onChange={() => setIsYearly(!isYearly)}
                  style={{ width: '3rem', height: '1.5rem', cursor: 'pointer' }}
                />
              </div>
              <span className={`fw-semibold ${isYearly ? 'text-primary' : 'text-muted'}`}>
                {t('dashboard.index.children.subscribe_modal.yearly') || 'Năm'}
              </span>
              <span className="badge bg-primary">
                {t('dashboard.index.children.subscribe_modal.save_percent', { percent: family.yearly_subscription_discount || 20 }) || `Tiết kiệm ${family.yearly_subscription_discount || 20}%`}
              </span>
            </div>

            {/* Pricing Cards - always vertical stack */}
            <div className="row g-3" style={{ marginTop: '12px' }}>
              <div className="col-12">
                <PricingCard
                  pkg="pro"
                  badge={t('dashboard.index.children.subscribe_modal.most_popular') || 'Phổ Biến Nhất'}
                />
              </div>
              <div className="col-12">
                <PricingCard
                  pkg="premium"
                  badge={t('dashboard.index.children.subscribe_modal.most_complete') || 'Đầy Đủ Nhất'}
                />
              </div>
            </div>

            {/* Gift Code Section */}
            <div className="mt-4 text-center">
              <GiftCodeSection
                childId={child.id}
                onRedeemSuccess={() => window.location.reload()}
                t={t}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
